<script setup lang="ts">
import usePaginator from "vue-use-paginator";

const { locale } = useI18n();
const { getOwnershipHistory } = useCollectiblesController();
const userStore = useUserStore();
const isOpen = ref(false);
const isLoading = ref(false);

const props = defineProps({
  id: {
    type: String,
    required: false,
    default: null,
  },
});

const ownershipHistory = reactive<OwnershipHistory>({
  records: [],
  total: 0,
});

const { page, pageSize, buttons, numItems, hasPrev, hasNext, goPrev, goNext } =
  usePaginator({
    pageSize: 5,
    numItems: ownershipHistory.total,
    numButtons: 6,
  });

const recordsGroupedByDate = computed(() => {
  return ownershipHistory.records.reduce((acc: any, curr: any) => {
    const date = new Date(curr.time).toLocaleDateString(locale.value, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(curr);
    return acc;
  }, {});
});

const uniqueTxDates = computed(() => {
  return Object.keys(recordsGroupedByDate.value);
});

const totalPages = computed(() => {
  return Math.ceil(ownershipHistory?.total / pageSize.value);
});

const resultStart = computed(() => {
  if (page.value == 1) {
    return ownershipHistory?.total < 1 ? 0 : 1;
  } else {
    return (page.value - 1) * pageSize.value + 1;
  }
});

const resultEnd = computed(() => {
  let resultEnd = 0;
  if (page.value == 1) {
    resultEnd = ownershipHistory.records.length;
  } else {
    resultEnd = page.value * pageSize.value;
  }
  if (resultEnd > ownershipHistory?.total) {
    resultEnd = ownershipHistory?.total;
  }
  return resultEnd;
});

const getHistory = async () => {
  if (!props.id) {
    ownershipHistory.records = [];
    ownershipHistory.total = 0;
    numItems.value = 0;
    return;
  }
  if (props.id === "GFAL") {
    ownershipHistory.records = [
      {
        translationId: "main.ownershipHistory.mint",
        from: "GFAL",
        to: "GFAL",
        type: "MINT",
        time: "2025-01-01T00:00:00Z",
        amount: { GFAL: 0, USD: 0 },
      },
    ];
    ownershipHistory.total = 1;
    numItems.value = 1;
    return;
  }
  isLoading.value = true;
  try {
    const res = await getOwnershipHistory(props.id, page.value, pageSize.value);
    if (res) {
      ownershipHistory.records = res.records;
      ownershipHistory.total = res.total;
      numItems.value = res.total;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

watch(page, () => {
  getHistory();
});

getHistory();
</script>
<template>
  <div
    class="mt-8 w-full bg-neutral-800 text-neutral-200 border border-primary-900"
    :class="isOpen ? 'rounded-t-lg' : 'rounded-lg'">
    <div
      class="py-4 px-6 border-b border-primary-900 flex justify-between"
      @click="isOpen = !isOpen">
      <h1 class="text-xl font-bold">
        {{ $t("main.ownershipHistory.title") }}
      </h1>
      <NuxtIcon
        name="mdi:keyboard-arrow-down"
        class="w-7 h-7 text-neutral-300 transition-all duration-300 cursor-pointer shrink-0"
        :class="{ 'transform rotate-180': isOpen }" />
    </div>
  </div>
  <transition
    enter-active-class="transition ease duration-100"
    enter-from-class="transform opacity-0 -translate-y-5"
    enter-to-class="transform opacity-100 translate-y-0"
    leave-active-class="transition ease duration-100"
    leave-from-class="transform opacity-100 translate-y-0"
    leave-to-class="transform opacity-0 -translate-y-5">
    <div
      v-if="isOpen"
      class="w-full flex flex-col justify-center border border-primary-900 border-t-0 transition-all duration-300 bg-neutral-800 rounded-b-lg">
      <div
        class="border-b border-primary-900 leading-6 text-neutral-300 font-bold hidden lg:grid grid-cols-4 lg:grid-cols-12 px-3 lg:px-6 py-2">
        <div class="py-3 pr-8 col-span-3 lg:col-span-3">
          {{ $t("main.ownershipHistory.action") }}
        </div>
        <p class="py-3 text-left lg:block hidden lg:col-span-4">
          {{ $t("main.ownershipHistory.user") }}
        </p>
        <p class="py-3 lg:col-span-3">
          {{ $t("main.ownershipHistory.date") }}
        </p>
        <p class="ml-auto py-3 lg:col-span-2">
          {{ $t("main.ownershipHistory.price") }}
        </p>
      </div>
      <div
        v-if="isLoading"
        class="py-4 pl-3 pr-4 text-sm text-neutral-200 text-center font-medium h-full flex flex-col justify-center items-center">
        <LoaderSpinner size="2xl" class="mt-8" />
      </div>
      <div
        v-for="(date, dateIndex) in uniqueTxDates"
        v-else-if="ownershipHistory.total > 0"
        :key="dateIndex">
        <div
          class="px-6 text-left leading-6 text-gray-400 lg:hidden col-span-12 flex items-center">
          <div
            class="pt-4 pb-1 w-full truncate text-lg text-secondary-200 font-medium leading-6 flex items-start border-y border-neutral-500">
            {{ date }}
          </div>
        </div>

        <div
          v-for="(row, txIndex) in recordsGroupedByDate[date]"
          :key="txIndex"
          class="mx-6 lg:mx-6 py-4 lg:border-b lg:border-neutral-500 leading-6 text-neutral-300 lg:font-bold grid grid-cols-2 items-center lg:grid-cols-12">
          <div
            class="py-3 last-of-type:border-b last-of-type:border-neutral-500 lg:last-of-type:border-b-0 h-full flex flex-col lg:flex-row lg:items-center lg:gap-4 col-span-1 lg:col-span-3">
            <CommonUsername
              :id="row.to"
              font-size="text-base"
              class="lg:hidden" />
            <p
              class="font-normal leading-6 text-neutral-300 lg:text-neutral-200 text-ellipsis lg:overflow-visible truncate lg:truncate-none">
              {{ $t(`main.ownershipHistory.${row.type}`) }}
            </p>
          </div>
          <div
            class="group py-3 lg:py-2 leading-6 text-gray-400 lg:table-cell hidden lg:col-span-4">
            <div
              class="w-min-[145px] font-medium leading-6 text-neutral-300 flex items-center gap-2">
              <CommonUserCard
                :id="row.to"
                size="custom"
                font-size="text-base"
                custom-class="w-9"
                :special-border="userStore.isCurrentUserId(row.to)" />
            </div>
          </div>
          <div
            class="hidden lg:flex items-center gap-2 py-3 lg:py-2 leading-6 text-gray-400 lg:col-span-3">
            <div>
              <p class="font-medium leading-6 text-xs sm:text-base">
                {{
                  new Date(row.time).toLocaleDateString($i18n.locale, {
                    hour: "numeric",
                    minute: "numeric",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                }}
              </p>
            </div>
          </div>
          <div
            class="ml-auto lg:py-3 text-gray-400 lg:table-cell lg:col-span-2 h-12">
            <div v-if="row.amount" class="flex flex-col items-end">
              <p class="truncate font-medium">
                {{ roundNum(row.amount.GFAL ?? 0) }}
                <span class="lg:hidden">GFAL</span>
              </p>
              <p class="text-neutral-300 text-xs pt-1">
                ~ {{ roundNum(row.amount.USD ?? 0) }} USD
              </p>
            </div>
            <p v-else class="font-medium">N/A</p>
          </div>
        </div>
      </div>
      <div
        v-else
        class="h-96 py-4 pl-3 pr-4 text-sm text-neutral-200 font-medium flex items-center justify-center">
        {{ $t("main.ownershipHistory.noHistory") }}
      </div>
      <div
        v-if="!isLoading"
        class="w-full flex flex-col gap-4 sm:flex-row sm:flex-1 items-center justify-between px-6 py-3 border-t border-primary-900">
        <div>
          <p class="text-xs sm:text-sm text-neutral-300 font-light">
            {{ $t("main.ownershipHistory.showing") }}
            <span class="font-medium">{{ resultStart }}</span>
            {{ $t("main.ownershipHistory.to") }}
            <span class="font-medium">{{ resultEnd }}</span>
            {{ $t("main.ownershipHistory.of") }}
            <span class="font-medium">{{ ownershipHistory.total }}</span>
            {{ $t("main.ownershipHistory.results") }}
          </p>
        </div>
        <div>
          <ol
            v-if="totalPages > 1"
            class="isolate inline-flex rounded-md shadow-xs text-neutral-200"
            aria-label="Pagination">
            <li
              class="relative inline-flex items-center px-2 py-1.5 text-gray-400 hover:bg-primary-800 focus:outline-hidden border border-primary-900 cursor-pointer"
              :class="{ disabled: !hasPrev }"
              @click="goPrev()">
              {{ "<" }}
            </li>
            <li
              v-for="button in buttons"
              :key="button.page"
              class="inline-flex items-center px-4 py-1.5 text-xs sm:text-sm font-light text-neutral-200 hover:bg-primary-800 focus:z-20 focus:outline-hidden border border-primary-900 cursor-pointer"
              :class="{ 'bg-primary-900': button.active }"
              @click="page = button.page">
              {{ button.ellipsis ? "..." : button.page }}
            </li>
            <li
              class="relative inline-flex items-center px-2 py-1.5 text-gray-400 hover:bg-primary-800 focus:outline-hidden border border-primary-900 cursor-pointer"
              :class="{ disabled: !hasNext }"
              @click="goNext()">
              {{ ">" }}
            </li>
          </ol>
        </div>
      </div>
    </div>
  </transition>
</template>
